import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Input,
  InputProps,
  Button,
  FormField,
  Select,
  SpaceBetween,
  RadioGroup,
  Checkbox,
  Form,
  Container,
  ColumnLayout,
  ExpandableSection,
  Grid,
  Box,
} from "@cloudscape-design/components-themed";
import {
  LABELS,
  QR_PRESENTATION_CATEGORY_ITEMS,
  CREDIT_CARD_ORDER_TARGET_ITEMS,
  PLACE_APPLICATION,
} from "./constants";
import {
  COMMON_LABELS,
  DivisionDict,
  addBlank,
  getDivisionLabel,
} from "../default-constants";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  INITIAL_SEARCH_CRITERIA,
  setSearchCriteria,
} from "../../features/slices/merchant-list";
import {
  useGetCommercialMerchantTypesQuery,
  useGetDivisionQuery,
  useGetComprehensiveCompaniesQuery,
  useGetCommercialIndustriesQuery,
  useGetGwCompaniesQuery,
} from "../../features/api";
import {
  clearAllErrTxts,
  clearFormFieldErrTxt,
  createFormFieldErrTxts,
} from "../../features/error/validation-slice";
import {
  getErrorText,
  isLengthValid,
  isHalfNumberValid,
  isHalfNumberAndCharValid,
} from "../../utils/inputValidation";
import {
  ComprehensiveCompanySearchRequest,
  MerchantListSearchRequest,
  Division,
} from "../../features/api-interfaces";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import { useLocation } from "react-router-dom";
import { getComprehensiveMerchantCodeItems } from "../../utils/common";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { RootState } from "../../app/store";

interface ISearchBar {
  setIsSkipSearch: Dispatch<SetStateAction<boolean>>;
}

const SearchBar = (props: ISearchBar) => {
  const { setIsSkipSearch } = props;
  // ユーザ種別確認
  const hasAuthority = useHasAuthorityHook(PERMISSIONS.MASTER_EDIT);
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const { searchCriteria } = useAppSelector(
    (state: RootState) => state.merchantList
  );
  // 検索条件
  const [searchInput, setSearchInput] = useState<MerchantListSearchRequest>(
    INITIAL_SEARCH_CRITERIA
  );
  // 加盟店種別プルダウン
  const [merchantTypes, setMerchantTypes] = useState<DivisionDict[]>([]);
  // 業態プルダウン
  const [industryItems, setIndustryItems] = useState<DivisionDict[]>([]);
  const [errText, setErrText] = useState<string>();

  const { msg } = useAppSelector((state) => state.validation);

  const dispatch = useAppDispatch();
  // 加盟店種別
  const { data } = useGetCommercialMerchantTypesQuery();

  // URL取得
  const pathname = useLocation().pathname;

  // 業態
  const { data: industryData } = useGetCommercialIndustriesQuery(undefined, {
    skip: !isDocomoUser,
  });

  // サービス名一覧
  const [serviceNameList, setServiceNameList] = useState<DivisionDict[]>([]);

  // 検索条件チェック用
  const [searchInvalid, setSearchInvalid] = useState(false);

  // GW一覧データの取得
  const { data: gw } = useGetGwCompaniesQuery(undefined, {
    skip: !isDocomoUser,
  });
  // GW一覧データの加工(viewから取得しているため決済GW事業者コードをキーに重複を削除)
  const uniqueData = gw
    ? Array.from(
        new Map(
          gw.map((item) => [item.gatewayNo?.toString() ?? "", item])
        ).values()
      )
    : [];

  const gwListData = uniqueData
    ? addBlank(
        uniqueData.map((item) => ({
          label: item.gwName,
          value: item.gatewayNo?.toString() ?? "",
        }))
      )
    : [];

  // 加盟店種別情報を取得する
  useEffect(() => {
    if (data) {
      let tmpMerchantTypes = [];
      tmpMerchantTypes = data.map((d) => {
        return { value: String(d.typeNo), label: d.typeName };
      });
      setMerchantTypes(addBlank(tmpMerchantTypes));
    }
  }, [data]);

  // 業態を取得する
  useEffect(() => {
    if (industryData) {
      let tmpIndustryItems = [];
      tmpIndustryItems = industryData.map((d) => {
        return { label: d.industryName, value: d.industryCode };
      });
      setIndustryItems(addBlank(tmpIndustryItems));
    }
  }, [industryData]);

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const divisionData2 = useGetDivisionQuery("3");
  const contractServiceDivisions = divisionData2?.data as Division[];

  // 区分取得結果変換
  const getDivisionItems = (
    divisions: Division[] | undefined
  ): DivisionDict[] => {
    if (divisions !== undefined) {
      return divisions.map((division) => ({
        label: division.label,
        value: division.kbnCode,
      }));
    }
    return [];
  };

  const contractServiceKbnItems: DivisionDict[] = addBlank(
    getDivisionItems(contractServiceDivisions)
  );
  useEffect(() => {
    setServiceNameList(contractServiceKbnItems);
  }, [divisionData2, contractServiceKbnItems]);

  // 包括加盟店データを取得
  const comprehensiveCompanySearchRequest = {
    companyCode: "",
    invisibleComprehensiveCompanyNoList: [],
  } as ComprehensiveCompanySearchRequest;

  // 包括事業者一覧取得API
  const { data: comprehensiveMerchantListData } =
    useGetComprehensiveCompaniesQuery(comprehensiveCompanySearchRequest);
  const [comprehensiveMerchantListItems, setComprehensiveMerchantListItems] =
    useState<DivisionDict[]>([]);
  useEffect(() => {
    setComprehensiveMerchantListItems(
      addBlank(getComprehensiveMerchantCodeItems(comprehensiveMerchantListData))
    );
  }, [comprehensiveMerchantListData]);

  const { formFieldErrTxts } = useAppSelector((state) => state.validation);
  // * 検索条件ステート更新関数
  const handleSearchInputOnChange = (
    name: keyof MerchantListSearchRequest,
    value: string
  ) => {
    switch (name) {
      case "siteId":
      case "ipid":
        if (!isHalfNumberValid(value)) {
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR006", msgArgs: [] })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 11)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR059",
              msgArgs: ["11"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "corporateNumber":
      case "shopId":
        if (!isHalfNumberValid(value)) {
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR006", msgArgs: [] })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 13)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR059",
              msgArgs: ["13"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "dunsNo":
        if (!isHalfNumberValid(value)) {
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR006", msgArgs: [] })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 9)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR059",
              msgArgs: ["9"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "companyName":
        if (!isLengthValid(value, 30)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["30"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "merchantName":
        if (!isLengthValid(value, 15)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["15"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "storeName":
        if (!isLengthValid(value, 100)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["100"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "merchantManagementNumber":
        if (!isHalfNumberAndCharValid(value)) {
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR006", msgArgs: [] })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 20)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR059",
              msgArgs: ["20"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }

      case "creditCardMerchantNumber":
        if (!isHalfNumberValid(value)) {
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR006", msgArgs: [] })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 30)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR059",
              msgArgs: ["30"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "shopCode":
      case "businessCode":
        if (!isLengthValid(value, 20) || !isHalfNumberAndCharValid(value)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR065",
              msgArgs: ["半角英数字", "20"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }

      // Orange案件追加
      //  決済グループID
      case "settlementGroupId":
        if (!isHalfNumberAndCharValid(value)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR164",
              msgArgs: ["半角英数字"],
            })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 26)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["26"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      //  Access Key
      case "accessKey":
        if (!isHalfNumberAndCharValid(value)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR164",
              msgArgs: ["半角英数字"],
            })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 26)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["26"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
    }
    setSearchInput({ ...searchInput, [name]: value });
  };

  // 画面遷移する際にエラーメッセージの初期化
  useEffect(() => {
    dispatch(clearAllErrTxts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pathname]);

  useEffect(() => {
    setSearchInput(searchCriteria);
  }, [searchCriteria]);

  /**
   * 検索ボタンを押したときの処理.
   * @param {CustomEvent<ClickDetail>} e
   */
  const handleOnSubmit = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();

    //バリデーションチェック
    setSearchInvalid(false);
    handleSearchInputOnChange("corporateNumber", searchInput.corporateNumber);
    handleSearchInputOnChange("dunsNo", searchInput.dunsNo);
    handleSearchInputOnChange("companyName", searchInput.companyName);
    handleSearchInputOnChange("merchantName", searchInput.merchantName);
    handleSearchInputOnChange("storeName", searchInput.storeName);
    handleSearchInputOnChange("shopCode", searchInput.shopCode);
    handleSearchInputOnChange("businessCode", searchInput.businessCode);
    handleSearchInputOnChange("ipid", searchInput.ipid);
    handleSearchInputOnChange("siteId", searchInput.siteId);
    handleSearchInputOnChange(
      "merchantManagementNumber",
      searchInput.merchantManagementNumber
    );
    handleSearchInputOnChange(
      "settlementGroupId",
      searchInput.settlementGroupId
    );
    handleSearchInputOnChange("accessKey", searchInput.accessKey);
    if (searchInvalid) return;

    setIsSkipSearch(false);
    dispatch(
      setSearchCriteria({ ...searchInput, timestamp: new Date().getTime() })
    );
    setErrText("");
  };

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setIsSkipSearch(true);
    setSearchInput(INITIAL_SEARCH_CRITERIA);
    dispatch(clearAllErrTxts());
  };

  return (
    <Form
      actions={
        <Button variant="normal" onClick={handleOnSubmit}>
          {COMMON_LABELS.BTNS.SEARCH}
        </Button>
      }
      errorText={errText}
    >
      <Container
        header={
          <ColumnLayout>
            <Box float="right">
              <Button variant="normal" onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </Box>
          </ColumnLayout>
        }
      >
        <SpaceBetween direction="vertical" size="xxl">
          <SpaceBetween direction="vertical" size="xxxs">
            <ColumnLayout columns={4}>
              <FormField label="サービス名">
                <Select
                  selectedOption={{
                    value: searchInput.serviceCode,
                    label: getDivisionLabel(
                      serviceNameList,
                      searchInput.serviceCode
                    ),
                  }}
                  options={serviceNameList}
                  onChange={({ detail: { selectedOption } }) =>
                    setSearchInput({
                      ...searchInput,
                      serviceCode: selectedOption.value!,
                    })
                  }
                />
              </FormField>
              {isDocomoUser && (
                <FormField label="契約ルート">
                  <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                    <RadioGroup
                      items={[
                        {
                          value: "",
                          label: LABELS.ALL,
                        },
                        {
                          value: "01",
                          label: LABELS.CONTRACT_DIRECTLY,
                        },
                        {
                          value: "02",
                          label: LABELS.CONTRACT_COMPREHENSIVE,
                        },
                      ]}
                      // 契約形態
                      onChange={({ detail }) => {
                        setSearchInput({
                          ...searchInput,
                          contractType: detail.value,
                          contractRoute: "",
                        });
                      }}
                      value={searchInput.contractType}
                    />
                    <Select
                      // 契約ルート(包括加盟店)
                      disabled={searchInput.contractType !== "02"}
                      selectedOption={{
                        value: searchInput.contractRoute,
                        label: getDivisionLabel(
                          comprehensiveMerchantListItems,
                          searchInput.contractRoute
                        ),
                      }}
                      options={comprehensiveMerchantListItems}
                      filteringType="auto"
                      onChange={({ detail: { selectedOption } }) =>
                        setSearchInput({
                          ...searchInput,
                          contractRoute: selectedOption.value!,
                        })
                      }
                    />
                  </Grid>
                </FormField>
              )}
            </ColumnLayout>
            <ColumnLayout columns={4}>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box variant="strong">{LABELS.COMPANY_NAME}</Box>
                    <Box variant="small">
                      {COMMON_LABELS.CONTENTS.COMPANY_LINKED_CPID}
                    </Box>
                  </SpaceBetween>
                }
                errorText={getErrorText("companyName", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.FONT_SIZE}
              >
                <Input
                  name="companyName"
                  value={searchInput.companyName}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("companyName", value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.IPID}
                errorText={getErrorText("ipid", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_11}
              >
                <Input
                  name="ipid"
                  value={searchInput.ipid}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("ipid", value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.SITE_ID}
                errorText={getErrorText("siteId", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_11}
              >
                <Input
                  name="siteId"
                  value={searchInput.siteId}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("siteId", value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.MERCHANT_MANAGEMENT_NUMBER}
                errorText={getErrorText(
                  "merchantManagementNumber",
                  formFieldErrTxts
                )}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_20}
              >
                <Input
                  name="merchantManagementNumber"
                  value={searchInput.merchantManagementNumber}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("merchantManagementNumber", value)
                  }
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={4}>
              <FormField
                label={LABELS.MERCHANTS_NAME}
                errorText={getErrorText("merchantName", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_15}
              >
                <Input
                  name="merchantName"
                  value={searchInput.merchantName}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("merchantName", value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.STORE_NAME}
                errorText={getErrorText("storeName", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_100}
              >
                <Input
                  name="storeName"
                  value={searchInput.storeName}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("storeName", value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.BUSINESS_CODE}
                errorText={getErrorText("businessCode", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_20}
              >
                <Input
                  name="businessCode"
                  value={searchInput.businessCode}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("businessCode", value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.SHOP_CODE}
                errorText={getErrorText("shopCode", formFieldErrTxts)}
                constraintText={PLACE_APPLICATION.TEXT_DIGITS_20}
              >
                <Input
                  name="shopCode"
                  value={searchInput.shopCode}
                  onChange={({ detail: { value } }) =>
                    handleSearchInputOnChange("shopCode", value)
                  }
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={1}>
              <Box float={"right"}>
                <FormField label={LABELS.EMPTY}>
                  <Checkbox
                    onChange={({ detail }) => {
                      setSearchInput({
                        ...searchInput,
                        excludeCancelledFlag: detail.checked,
                      });
                    }}
                    checked={searchInput.excludeCancelledFlag}
                  >
                    {LABELS.EXCLUDE_CANCELED}
                  </Checkbox>
                </FormField>
              </Box>
            </ColumnLayout>
          </SpaceBetween>
          <Container>
            <ExpandableSection headerText={LABELS.DETAIL_SEARCH}>
              {!isDocomoUser && (
                <ColumnLayout columns={4}>
                  <FormField
                    label={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="strong">{LABELS.CORPORATE_NUMBER}</Box>
                        <Box variant="small">
                          {COMMON_LABELS.CONTENTS.COMPANY_LINKED_CPID}
                        </Box>
                      </SpaceBetween>
                    }
                    errorText={getErrorText(
                      "corporateNumber",
                      formFieldErrTxts
                    )}
                    constraintText={PLACE_APPLICATION.TEXT_DIGITS_13}
                  >
                    <Input
                      name="corporateNumber"
                      value={searchInput.corporateNumber}
                      onChange={({
                        detail,
                      }: {
                        detail: InputProps.ChangeDetail;
                      }) =>
                        handleSearchInputOnChange(
                          "corporateNumber",
                          detail.value
                        )
                      }
                    />
                  </FormField>
                  <FormField
                    label={LABELS.QR_PRESENTATION_CATEGORY}
                    errorText={msg}
                  >
                    <Select
                      selectedOption={{
                        value: searchInput.qrPresentationCategory,
                        label: getDivisionLabel(
                          QR_PRESENTATION_CATEGORY_ITEMS,
                          searchInput.qrPresentationCategory
                        ),
                      }}
                      options={QR_PRESENTATION_CATEGORY_ITEMS}
                      filteringType={"none"}
                      onChange={({ detail: { selectedOption } }) =>
                        setSearchInput({
                          ...searchInput,
                          qrPresentationCategory: selectedOption.value!,
                        })
                      }
                    />
                  </FormField>
                </ColumnLayout>
              )}
              <ColumnLayout columns={4}>
                {isDocomoUser && (
                  <FormField
                    label={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="strong">{LABELS.CORPORATE_NUMBER}</Box>
                        <Box variant="small">
                          {COMMON_LABELS.CONTENTS.COMPANY_LINKED_CPID}
                        </Box>
                      </SpaceBetween>
                    }
                    errorText={getErrorText(
                      "corporateNumber",
                      formFieldErrTxts
                    )}
                    constraintText={PLACE_APPLICATION.TEXT_DIGITS_13}
                  >
                    <Input
                      name="corporateNumber"
                      value={searchInput.corporateNumber}
                      onChange={({ detail }) =>
                        handleSearchInputOnChange(
                          "corporateNumber",
                          detail.value
                        )
                      }
                    />
                  </FormField>
                )}
                {isDocomoUser && (
                  <FormField
                    label={LABELS.DUNS_NO}
                    errorText={getErrorText("dunsNo", formFieldErrTxts)}
                    constraintText={PLACE_APPLICATION.TEXT_DIGITS_9}
                  >
                    <Input
                      name="dunsNo"
                      value={searchInput.dunsNo}
                      onChange={({
                        detail,
                      }: {
                        detail: InputProps.ChangeDetail;
                      }) => handleSearchInputOnChange("dunsNo", detail.value)}
                    />
                  </FormField>
                )}
                {isDocomoUser && (
                  <FormField
                    label={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="strong">{LABELS.GW_COMPANY}</Box>
                      </SpaceBetween>
                    }
                    stretch={true}
                  >
                    <Select
                      selectedOption={{
                        value: searchInput.gwCompanyNo,
                        label: getDivisionLabel(
                          gwListData,
                          searchInput.gwCompanyNo
                        ),
                      }}
                      filteringType="auto"
                      onChange={({ detail: { selectedOption } }) => {
                        setSearchInput({
                          ...searchInput,
                          gwCompanyNo: selectedOption.value!,
                        });
                      }}
                      options={gwListData}
                      selectedAriaLabel="Selected"
                    />
                  </FormField>
                )}
              </ColumnLayout>
              <ColumnLayout columns={2}>
                <ColumnLayout columns={2}>
                  {isDocomoUser && (
                    <FormField label={LABELS.MERCHANTS_TYPE} errorText={msg}>
                      <Select
                        selectedOption={{
                          value: searchInput.merchantTypeNo,
                          label: getDivisionLabel(
                            merchantTypes,
                            searchInput.merchantTypeNo
                          ),
                        }}
                        options={merchantTypes}
                        filteringType={"none"}
                        onChange={({ detail: { selectedOption } }) =>
                          setSearchInput({
                            ...searchInput,
                            merchantTypeNo: selectedOption.value!,
                          })
                        }
                      />
                    </FormField>
                  )}
                  {isDocomoUser && (
                    <FormField
                      label={LABELS.QR_PRESENTATION_CATEGORY}
                      errorText={msg}
                    >
                      <Select
                        selectedOption={{
                          value: searchInput.qrPresentationCategory,
                          label: getDivisionLabel(
                            QR_PRESENTATION_CATEGORY_ITEMS,
                            searchInput.qrPresentationCategory
                          ),
                        }}
                        options={QR_PRESENTATION_CATEGORY_ITEMS}
                        filteringType={"none"}
                        onChange={({ detail }) =>
                          setSearchInput({
                            ...searchInput,
                            qrPresentationCategory:
                              detail.selectedOption.value!,
                          })
                        }
                      />
                    </FormField>
                  )}
                </ColumnLayout>
                {isDocomoUser && (
                  <FormField label={LABELS.INDUSTRY} errorText={msg}>
                    <Select
                      selectedOption={{
                        value: searchInput.industryCode,
                        label: getDivisionLabel(
                          industryItems,
                          searchInput.industryCode
                        ),
                      }}
                      options={industryItems}
                      filteringType={"auto"}
                      onChange={({ detail: { selectedOption } }) =>
                        setSearchInput({
                          ...searchInput,
                          industryCode: selectedOption.value!,
                        })
                      }
                    />
                  </FormField>
                )}
              </ColumnLayout>
              {hasAuthority && (
                <ColumnLayout columns={2}>
                  <ColumnLayout columns={2}>
                    <FormField
                      label={LABELS.SHOP_ID}
                      errorText={getErrorText("shopId", formFieldErrTxts)}
                      constraintText={PLACE_APPLICATION.TEXT_DIGITS_13}
                    >
                      <Input
                        name={"shopId"}
                        value={
                          searchInput.shopId !== undefined
                            ? String(searchInput.shopId)
                            : ""
                        }
                        onChange={({
                          detail,
                        }: {
                          detail: InputProps.ChangeDetail;
                        }) => handleSearchInputOnChange("shopId", detail.value)}
                      />
                    </FormField>
                    <FormField
                      label={LABELS.CREDIT_CARD_ORDER_TARGET}
                      errorText={msg}
                    >
                      <Select
                        selectedOption={{
                          value: searchInput.creditCardOrderTarget,
                          label: getDivisionLabel(
                            CREDIT_CARD_ORDER_TARGET_ITEMS,
                            searchInput.creditCardOrderTarget
                          ),
                        }}
                        options={CREDIT_CARD_ORDER_TARGET_ITEMS}
                        filteringType={"none"}
                        onChange={({ detail: { selectedOption } }) =>
                          setSearchInput({
                            ...searchInput,
                            creditCardOrderTarget: selectedOption.value!,
                          })
                        }
                      />
                    </FormField>
                  </ColumnLayout>
                  <FormField
                    label={LABELS.CREDIT_CARD_MERCHANT_NUMBER}
                    errorText={getErrorText(
                      "creditCardMerchantNumber",
                      formFieldErrTxts
                    )}
                    constraintText={PLACE_APPLICATION.TEXT_DIGITS_30}
                  >
                    <Input
                      name="creditCardMerchantNumber"
                      value={searchInput.creditCardMerchantNumber}
                      onChange={({
                        detail,
                      }: {
                        detail: InputProps.ChangeDetail;
                      }) =>
                        handleSearchInputOnChange(
                          "creditCardMerchantNumber",
                          detail.value
                        )
                      }
                    />
                  </FormField>
                </ColumnLayout>
              )}
              {hasAuthority && (
                <ColumnLayout columns={2}>
                  <ColumnLayout columns={2}>
                    <FormField
                      label={LABELS.SETTLEMENT_GROUP_ID}
                      errorText={getErrorText(
                        "settlementGroupId",
                        formFieldErrTxts
                      )}
                      constraintText={PLACE_APPLICATION.TEXT_DIGITS_26}
                    >
                      <Input
                        name={"settlementGroupId"}
                        value={
                          searchInput.settlementGroupId !== undefined
                            ? String(searchInput.settlementGroupId)
                            : ""
                        }
                        onChange={({
                          detail,
                        }: {
                          detail: InputProps.ChangeDetail;
                        }) =>
                          handleSearchInputOnChange(
                            "settlementGroupId",
                            detail.value
                          )
                        }
                      />
                    </FormField>
                    <FormField
                      label={LABELS.ACCESS_KEY}
                      errorText={getErrorText("accessKey", formFieldErrTxts)}
                      constraintText={PLACE_APPLICATION.TEXT_DIGITS_26}
                    >
                      <Input
                        name={"accessKey"}
                        value={
                          searchInput.accessKey !== undefined
                            ? String(searchInput.accessKey)
                            : ""
                        }
                        onChange={({
                          detail,
                        }: {
                          detail: InputProps.ChangeDetail;
                        }) =>
                          handleSearchInputOnChange("accessKey", detail.value)
                        }
                      />
                    </FormField>
                  </ColumnLayout>
                </ColumnLayout>
              )}
            </ExpandableSection>
          </Container>
        </SpaceBetween>
      </Container>
    </Form>
  );
};

export default SearchBar;
